import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Select, InputNumber, Input, Switch, Collapse, Button, Spin, Typography, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconHome2,
  IconEngine,
  IconSparkles,
  IconKey,
  IconCamera,
  IconConfetti,
  IconX,
  IconChevronDown,
  IconUserCog,
  IconTruckDelivery,
  IconTool,
  IconCopy,
} from '@tabler/icons-react';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import StaticMap from '../../components/StaticMap';
import PhotoUpload from '../../components/PhotoUpload';
import VideoUpload from '../../components/VideoUpload';
import EmojiTextArea from '../../components/EmojiTextArea';
import FloatingLabelInput from '../../components/FloatingLabelInput';
import WizardTileGrid from '../../components/WizardTileGrid';
import TaxonomyPicker from '../../components/TaxonomyPicker';
import WizardIllustration from '../../components/WizardIllustration';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import type { HeavyDutyMachineryOptionIcon } from '../../constants/heavyDutyMachineryOptionIcons';
import {
  getHeavyDutyMachineryById,
  getMyHeavyDutyMachineries,
  createHeavyDutyMachinery,
  updateHeavyDutyMachinery,
} from '../../services/HeavyDutyMachineryService';
import { useHostProfile } from '../../contextProviders/HostProfileProvider';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { getCountries, ICountryResponse } from '../../services/LocaleService';
import { ICreateHeavyDutyMachineryRequest, IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { WIZARD_REINFORCEMENT_MESSAGES } from '../../constants/wizardReinforcementMessages';
import ManufacturerBrandSelect from '../../components/ManufacturerBrandSelect';
import OperatorAssignmentPicker from '../../components/OperatorAssignmentPicker';
import AccessoryAssignmentPicker from '../../components/AccessoryAssignmentPicker';
import InsurancePolicyAssignmentPicker from '../../components/InsurancePolicyAssignmentPicker';
import { getMyOperators } from '../../services/OperatorService';
import { IOperator } from '../../interfaces/heavyDutyMachinery/IOperator';
import { getMyMachineryAccessories } from '../../services/MachineryAccessoryService';
import { IMachineryAccessory } from '../../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { getMyHostInsurancePolicies } from '../../services/HostInsurancePolicyService';
import { IHostInsurancePolicy } from '../../interfaces/heavyDutyMachinery/IHostInsurancePolicy';
import { getMyTransportVehicles } from '../../services/TransportVehicleService';
import { ITransportVehicle } from '../../interfaces/heavyDutyMachinery/ITransportVehicle';
import { getMachinerySpecConfig } from '../../constants/machinerySpecsByCategory';
import { PROPULSION_TYPE_OPTIONS, FUEL_TYPE_OPTIONS } from '../../constants/machineryGlobalSpecOptions';
import { getCustomSpecFields } from '../../constants/machineryCustomSpecFieldsByType';
import MachineryDraftAssistant, { MachineryDraftApplied } from '../../components/machinery/MachineryDraftAssistant';
import { IMachineryPlateResult } from '../../interfaces/heavyDutyMachinery/IMachineryAssist';
import AiFilledMark from '../../components/machinery/AiFilledMark';
import { applyMachineryDraft, applyPlateRead, isBlankFormValue, AI_CHECK_FIELDS } from '../../helpers/machineryDraftHelper';
import {
  inheritFromListing,
  mostRecentListing,
  revertInherited,
  unitIdentityFor,
  MAX_UNITS,
} from '../../helpers/machineryPublishHelper';
import { TEAL, TEAL_SOFT_BG, SURFACE_1_SOFT, WIZARD_TONE_EMERALD, WIZARD_TONE_OCHRE, WIZARD_TONE_PINE, WIZARD_TONE_TERRACOTTA, WIZARD_TONE_OLIVE, WIZARD_TONE_TAUPE, WIZARD_TONE_DEEP_TEAL } from '../../theme/creamyTokens';
import {
  HIRE_MODE_OPTIONS,
  HIRE_MODE_LIGHT_OPTIONS,
  ACCESS_TYPE_WIZARD_OPTIONS,
  RENTAL_MODE_WIZARD_OPTIONS,
  RENTAL_MODE_LIGHT_OPTIONS,
  PARKING_OPTIONS,
  CANCELLATION_POLICY_OPTIONS,
  TIMEZONE_OPTIONS,
  HeavyDutyMachineryOption,
} from '../../constants/heavyDutyMachinerySpaceOptions';

const { Text } = Typography;

// ============================================================================
// TEMPORARY -- requested by the user to test the end-to-end flow without
// filling in every required field first. Set back to `false` to restore
// normal per-phase/submit validation (goNext/handleSubmit below both check
// this before calling form.validateFields). REMOVE this flag entirely (and
// the two `if` guards that reference it) once testing is done -- it was
// never meant to ship.
// ============================================================================
const SKIP_WIZARD_VALIDATION_TEMP = true;

interface HeavyDutyMachineryFormValues extends Omit<ICreateHeavyDutyMachineryRequest, 'photos' | 'photoThumbnails' | 'videos' | 'dryHireAvailable' | 'wetHireHostOperatorAvailable' | 'wetHireThirdPartyAvailable' | 'deliveryAvailable' | 'operatorIds' | 'accessoryIds' | 'insurancePolicyIds' | 'deliveryVehicleId'> {
  photosText?: string[];
  photoThumbnailsText?: string[];
  videosList?: string[];
  // Tile values are HIRE_MODE_OPTIONS' own values (the exact boolean field
  // names) -- mapped to/from dryHireAvailable/wetHireHostOperatorAvailable/
  // wetHireThirdPartyAvailable/deliveryAvailable at load/submit time, same
  // trick as any other checklist field here.
  hireModesList?: string[];
  // One Form.Item per machineryCustomSpecFieldsByType.ts field def (name
  // ['customSpecs', key]) -- collapsed into the single customAttributesJson
  // string at submit time, same "form holds the structured shape, request
  // gets the wire shape" split as hireModesList above.
  customSpecs?: Record<string, string | number | boolean>;
  // OperatorAssignmentPicker's own value shape -- IOperator.id strings,
  // same convention as HeavyDutyMachineryBookingWidget's own
  // selectedAccessoryIds/selectedOperatorId. Mapped to the wire's
  // number[] (ICreateHeavyDutyMachineryRequest.operatorIds) at submit time.
  operatorIds?: string[];
  // AccessoryAssignmentPicker/InsurancePolicyAssignmentPicker's own value
  // shape -- catalog item id strings, same convention as operatorIds above.
  // Mapped to the wire's number[] (accessoryIds/insurancePolicyIds) at
  // submit time.
  accessoryIds?: string[];
  insurancePolicyIds?: string[];
  // PillSelectField's own value shape -- an ITransportVehicle.id string,
  // same convention as operatorIds above. Mapped to the wire's number
  // (ICreateHeavyDutyMachineryRequest.deliveryVehicleId) at submit time.
  deliveryVehicleId?: string;
}

type PhaseKey = 'story' | 'locationSpecs' | 'operator' | 'delivery' | 'extras' | 'accessRules' | 'rulesPhotos';

interface PhaseConfig {
  key: PhaseKey;
  titleKey: string;
  subtitleKey: string;
  icon: HeavyDutyMachineryOptionIcon;
  background: string;
  /**
   * Undefined = always in the flow. 'operator'/'delivery' only appear once
   * the matching hire mode is checked back in locationSpecs, and neither
   * appears at all for light equipment: nobody hires an operator to run a
   * hedge trimmer, and nobody sends a lowloader for one either (see
   * ITaxonomyType.isLightEquipment). Evaluated
   * fresh every render (see visiblePhases below) against the wizard's
   * current hasWetHire/hasDelivery state, so ticking or unticking the
   * checkbox adds or drops the phase from "Continuar"'s flow immediately,
   * no separate reconciliation needed.
   */
  visible?: (ctx: { hasWetHire: boolean; hasDelivery: boolean; isLightEquipment: boolean }) => boolean;
}

// One dark background per phase (see the WIZARD_TONE_* tokens) -- purely a
// visual cue that this is a different chapter, no other meaning attached to
// the colours. They alternate cool and warm down the list on purpose: two
// consecutive steps that look alike tell a host nothing about having moved
// on.
// 4 phases (down from the original 7 spa-era ones): 'location' + 'specs' +
// 'hireModes' are now one 'locationSpecs' phase -- the wizard's most
// important screen (where it is, what it is, how it can be rented, all
// together) -- and 'ambiance' (view privacy + underwater/garden lighting)
// is gone entirely, it never applied to machinery.
//
// 'story' (title/description) is LAST, not first: asking for a title/
// description of a machine the host hasn't described yet made no sense in
// practice (see this reorder's own task) -- by the time they reach it every
// other phase is already answered, so the title autogeneration effect below
// has had every input it needs since Paso 1 and this phase just displays
// what's already sitting in the form store, not a fresh computation.
const PHASES: PhaseConfig[] = [
  { key: 'locationSpecs', titleKey: TRANSLATIONS.WIZARD_PHASE_LOCATION_SPECS_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_LOCATION_SPECS_SUBTITLE, icon: IconEngine, background: WIZARD_TONE_EMERALD },
  // Both conditional -- only ever shown right after their own checkbox
  // (see HIRE_MODE_OPTIONS in locationSpecs) is on, so a host who offers
  // neither never sees either extra screen.
  { key: 'operator', titleKey: TRANSLATIONS.WIZARD_PHASE_OPERATOR_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_OPERATOR_SUBTITLE, icon: IconUserCog, background: WIZARD_TONE_OCHRE, visible: ({ hasWetHire, isLightEquipment }) => hasWetHire && !isLightEquipment },
  { key: 'delivery', titleKey: TRANSLATIONS.WIZARD_PHASE_DELIVERY_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_DELIVERY_SUBTITLE, icon: IconTruckDelivery, background: WIZARD_TONE_PINE, visible: ({ hasDelivery, isLightEquipment }) => hasDelivery && !isLightEquipment },
  // Always in the flow, unlike operator/delivery above -- there's no single
  // checkbox this depends on, and an empty catalog already renders its own
  // "add one first" EmptyState inside each picker rather than needing the
  // whole phase hidden.
  { key: 'extras', titleKey: TRANSLATIONS.WIZARD_PHASE_EXTRAS_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_EXTRAS_SUBTITLE, icon: IconTool, background: WIZARD_TONE_TERRACOTTA },
  { key: 'accessRules', titleKey: TRANSLATIONS.WIZARD_PHASE_ACCESS_RULES_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_ACCESS_RULES_SUBTITLE, icon: IconKey, background: WIZARD_TONE_OLIVE },
  { key: 'rulesPhotos', titleKey: TRANSLATIONS.WIZARD_PHASE_RULES_PHOTOS_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_RULES_PHOTOS_SUBTITLE, icon: IconCamera, background: WIZARD_TONE_TAUPE },
  { key: 'story', titleKey: TRANSLATIONS.WIZARD_PHASE_STORY_TITLE, subtitleKey: TRANSLATIONS.WIZARD_PHASE_STORY_SUBTITLE, icon: IconHome2, background: WIZARD_TONE_DEEP_TEAL },
];

// Exactly the same required fields the old single-page form validated at
// submit time -- just grouped so "Continuar" can validate one phase's worth
// instead of waiting for the very end. Never touched: no field gained or
// lost a `required` rule by moving in here.
//
// A function rather than a static map: pricePerHour/pricePerNight and
// minBookingDurationMinutes/maxBookingDurationMinutes are each only rendered
// (and only actually required) for specific rentalMode values; weightTons/
// powerHp/reachMeters vary by category (see machinerySpecsByCategory); and
// maxOperators only applies when some wet-hire mode is on offer -- validateFields
// needs their exact names in the list it's given, or a still-empty conditional
// field silently sails past "Continuar" and only fails once the backend
// rejects the payload. Day pass is no longer a wizard choice (see
// RENTAL_MODE_WIZARD_OPTIONS) so pricePerDayPass never needs to be in here.
const getFieldsForPhase = (
  phaseKey: PhaseKey,
  rentalMode?: string,
  category?: string,
  hasWetHire?: boolean,
  isLightEquipment?: boolean
): (keyof HeavyDutyMachineryFormValues)[] => {
  switch (phaseKey) {
    // title isn't required here -- see Tarea A's autogeneration effect above,
    // which fills it in once category/brand/model/city (Paso 2) are known;
    // the host can still type their own before then, or edit it after.
    case 'story':
      return ['description'];
    case 'locationSpecs': {
      const fields: (keyof HeavyDutyMachineryFormValues)[] = ['city', 'category'];
      const specConfig = getMachinerySpecConfig(category, isLightEquipment);
      if (specConfig.weightTons === 'required') fields.push('weightTons');
      if (specConfig.powerHp === 'required') fields.push('powerHp');
      if (specConfig.reachMeters === 'required') fields.push('reachMeters');
      return fields;
    }
    case 'operator':
      return hasWetHire ? ['maxOperators'] : [];
    case 'delivery':
      return [];
    case 'extras':
      return [];
    case 'accessRules': {
      const fields: (keyof HeavyDutyMachineryFormValues)[] = ['accessType', 'rentalMode', 'parking'];
      if (rentalMode === 'Hourly' || rentalMode === 'Both') {
        fields.push('pricePerHour', 'bufferMinutes', 'minAdvanceBookingHours', 'slotGranularityMinutes', 'minBookingDurationMinutes', 'maxBookingDurationMinutes');
      }
      if (rentalMode === 'Overnight' || rentalMode === 'Both') fields.push('pricePerNight', 'maxAdvanceBookingDays');
      return fields;
    }
    case 'rulesPhotos':
      return ['cancellationPolicy', 'timezone'];
    default:
      return [];
  }
};

/**
 * What the wizard starts every field at.
 *
 * Pulled out of the Form's own initialValues so that one other thing can read
 * it: copying a host's previous listing has to tell "nobody has answered this"
 * apart from "the host chose this", and a field still holding one of these has
 * not been answered by anybody -- see inheritFromListing. Undoing that copy
 * reads it too, to put a required field back to something rather than to
 * nothing.
 */
const WIZARD_DEFAULTS = {
  maxOperators: 2,
  bufferMinutes: 30,
  minAdvanceBookingHours: 2,
  maxAdvanceBookingDays: 60,
  slotGranularityMinutes: 30,
  cancellationPolicy: 'Moderate',
  timezone: 'Europe/Madrid',
  restrictDeliveryToSameCountry: false,
  chargeStaffTimeForDelivery: false,
  // Encendida de salida: vender una máquina sin mirar antes el trabajo es el
  // caso raro, no el normal -- el precio depende del transporte, del operario
  // y de cómo esté la obra. El host puede apagarla en el mismo formulario.
  // Tiene que estar aquí además de en el backend: este valor inicial se envía
  // siempre, así que el defecto del servidor nunca llegaría a aplicarse.
  deferredBookingEnabled: true,
};

/** Confetti fired near the specs phase's icon badge, on the left panel side. */
const fireSpecsConfetti = () => {
  confetti({ particleCount: 50, spread: 60, startVelocity: 28, scalar: 0.85, origin: { x: 0.16, y: 0.35 }, colors: ['#bfe9ff', '#ffffff', '#3ba7d9'] });
};

/** Confetti fired near wherever the photo grid sits, on the right panel side. */
const firePhotoConfetti = () => {
  confetti({ particleCount: 36, spread: 50, scalar: 0.75, origin: { x: 0.6, y: 0.55 } });
};

/** The one deliberately loud animation in the whole wizard -- a few seconds
 * of side cannons plus a big central burst, right as the space goes live. */
const firePublishConfetti = () => {
  const end = Date.now() + 1400;
  const colors = ['#000000', '#ffffff', '#bfe9ff', '#3ba7d9'];
  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 55, startVelocity: 45, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 5, angle: 120, spread: 55, startVelocity: 45, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 160, spread: 100, startVelocity: 35, origin: { y: 0.6 }, colors });
};

/** Small reinforcement card shown right under a field once it holds a value
 * this app has specific, non-generic copy for (see wizardReinforcementMessages) --
 * silently renders nothing for any value without one. Tinted teal (rather than
 * a neutral fill) so it reads as this wizard's second accent alongside the
 * coral of its buttons/selected tiles, echoing the progress bar's own teal. */
const FieldReinforcement: React.FC<{ field: string; value?: string }> = ({ field, value }) => {
  const { t } = useTranslation();
  const messageKey = value ? WIZARD_REINFORCEMENT_MESSAGES[field]?.[value] : undefined;

  return (
    <AnimatePresence mode="wait">
      {messageKey && (
        <motion.div
          key={messageKey}
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          style={{
            marginTop: 12,
            padding: '14px 16px',
            borderRadius: 14,
            background: TEAL_SOFT_BG,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <IconSparkles size={16} color={TEAL} style={{ flexShrink: 0, marginTop: 2 }} />
          <Text style={{ fontSize: 13 }}>{translate(t, messageKey)}</Text>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Small fixed label + pill input, for every numeric field in the wizard --
// FloatingLabelInput/Number's floating label assumes it always fits on one
// line, which broke down for this form's longer field names (e.g. "How far
// ahead bookings are allowed") wrapping onto a second line and overlapping
// the value once floated. A plain caption above never has that problem
// regardless of label length, and still reads as a pill input. Also covers
// the wizard's currency fields specifically because addonAfter nests the
// real .ant-input-number in its own extra group wrapper that the floating
// CSS never targeted either way.
const PillNumberField: React.FC<{
  // A node rather than a string so a field can carry the publishing
  // assistant's own mark next to its caption (see AiFilledMark) without every
  // call site having to rebuild this control's layout.
  label: React.ReactNode;
  min?: number;
  step?: number;
  addonAfter?: string;
  disabled?: boolean;
  value?: number;
  onChange?: (value: number | null) => void;
}> = ({ label, value, onChange, addonAfter, ...rest }) => (
  <div>
    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
      {label}
    </Text>
    <InputNumber
      {...rest}
      addonAfter={addonAfter}
      className={addonAfter ? 'wizard-currency-input' : 'wizard-number-input'}
      style={{ width: '100%' }}
      value={value}
      onChange={onChange}
      // Reported on-device: iOS's default keyboard (antd's InputNumber is a
      // plain text input under the hood) lets you type letters straight in
      // -- they don't parse as a number, so the value silently becomes null
      // once you leave the field, but nothing stops you typing them or
      // moving on to the next phase first. `inputMode="decimal"` swaps in
      // the numeric keypad (digits + one decimal separator, no letter keys
      // at all) on every mobile browser, which removes the typo path
      // entirely instead of trying to catch it after the fact.
      inputMode="decimal"
    />
  </div>
);

// Same "caption above, pill control below" shape as PillNumberField, for a
// Select-backed field instead of a number -- the delivery phase's own
// deliveryVehicleId field, whose options come from the host's own fetched
// fleet rather than a typed number.
const PillSelectField: React.FC<{
  label: string;
  options: { value: string | number; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  value?: string | number;
  onChange?: (value: string | number) => void;
}> = ({ label, value, onChange, options, placeholder, disabled }) => (
  <div>
    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
      {label}
    </Text>
    <Select
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      style={{ width: '100%' }}
      value={value}
      onChange={onChange}
    />
  </div>
);

const FieldGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 40 }}>
    <Text strong style={{ display: 'block', fontSize: 20, marginBottom: 16 }}>
      {label}
    </Text>
    {children}
  </div>
);

const HeavyDutyMachineryForm: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [form] = Form.useForm<HeavyDutyMachineryFormValues>();
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [celebration, setCelebration] = useState<IHeavyDutyMachinery | undefined>(undefined);
  const firstPhotoCelebratedRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // The scrollable fields area (see contentRef's own div below) keeps
  // whatever scrollTop it had before Continuar/Atrás swapped in the next
  // phase's fields -- it's the same DOM element throughout, only its
  // children change, so the browser has no reason to reset scroll on its
  // own. Landing mid-scroll (or at the bottom) on a phase you haven't seen
  // yet reads as broken, not just visually startling.
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [phaseIndex]);

  const { hasHostProfile, loading: hostProfileLoading } = useHostProfile();
  // Not Form.useWatch: rentalMode is read on two different phases
  // (accessRules, for the price fields it gates, and rulesPhotos, for the
  // booking-duration fields it also gates) -- once accessRules's own
  // rentalMode Form.Item unmounts on leaving that phase, useWatch stops
  // reflecting the stored value (same antd quirk HostOnboardingWizard's own
  // accountType/businessCategory hit for the same reason -- see its doc
  // comment). onValuesChange fires off the Form instance itself regardless
  // of which Form.Item is currently mounted.
  const [rentalMode, setRentalMode] = useState<string | undefined>(undefined);
  // Both drive whether the 'operator'/'delivery' phases even exist in
  // visiblePhases (see PHASES) as well as fields still rendered directly in
  // locationSpecs -- tracked off onValuesChange rather than Form.useWatch so
  // they still reflect the chosen value once hireModesList's own Form.Item
  // (locationSpecs) has unmounted, same reasoning as rentalMode above.
  const [hasWetHire, setHasWetHire] = useState(false);
  const [hasDelivery, setHasDelivery] = useState(false);
  // Same onValuesChange-into-useState reasoning as hasWetHire/hasDelivery
  // above: category's own Form.Item lives in locationSpecs, but
  // OperatorAssignmentPicker (operator phase) needs its current value too,
  // to sort/badge qualified operators first -- Form.useWatch('category', ...)
  // is used elsewhere in this file only for effects/reads that happen while
  // locationSpecs is still mounted, never safe to rely on from a later phase.
  const [wizardCategory, setWizardCategory] = useState<string | undefined>(undefined);
  /**
   * Which fields the publishing assistant filled in, so each one can say so
   * (see AiFilledMark).
   *
   * Kept as names rather than as a flag on the whole form because that is the
   * question being asked at each input: not "was the assistant used" but "is
   * THIS number one I have to check". Anything the host typed themselves --
   * before or after -- never gets in here, since applyMachineryDraft refuses
   * to overwrite an answer and only reports what it actually set.
   */
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set());

  /**
   * What was copied across from the host's own previous listing, so undoing
   * it can put back exactly that and nothing else.
   */
  const [inheritedFields, setInheritedFields] = useState<string[]>([]);

  /**
   * How many identical machines are being published in one go.
   *
   * Asked at the very end rather than at the start: at the start it is one
   * more question before anything has happened, and at the end it is a
   * multiplier on work already done -- which is when "tengo tres" feels like
   * a shortcut instead of like a form.
   */
  const [unitCount, setUnitCount] = useState(1);
  const [operators, setOperators] = useState<IOperator[]>([]);
  const [operatorsLoading, setOperatorsLoading] = useState(true);
  const [accessories, setAccessories] = useState<IMachineryAccessory[]>([]);
  const [accessoriesLoading, setAccessoriesLoading] = useState(true);
  const [insurancePolicies, setInsurancePolicies] = useState<IHostInsurancePolicy[]>([]);
  const [insurancePoliciesLoading, setInsurancePoliciesLoading] = useState(true);
  const [transportVehicles, setTransportVehicles] = useState<ITransportVehicle[]>([]);
  const selectedCountryName = Form.useWatch('country', form);
  // Same onValuesChange-into-useState reasoning as rentalMode/hasWetHire
  // above: hostCurrencyCode's own Form.Item lives in locationSpecs, but its
  // value is also needed for the addonAfter of pricePerHour/pricePerNight,
  // which live in the later accessRules phase.
  const [hostCurrencyCode, setHostCurrencyCode] = useState<string | undefined>(undefined);
  const [countries, setCountries] = useState<ICountryResponse[]>([]);
  // countryCode's own Form.Item lives in locationSpecs too -- this only
  // needs to stay live while that phase is mounted, to drive the
  // auto-preselect effect below while the host is picking a location.
  const selectedCountryCode = Form.useWatch('countryCode', form);
  const selectedLatitude = Form.useWatch('latitude', form);
  const selectedLongitude = Form.useWatch('longitude', form);
  const photos = Form.useWatch('photosText', form);
  const photoThumbnails = Form.useWatch('photoThumbnailsText', form);
  const videos = Form.useWatch('videosList', form);
  const accessType = Form.useWatch('accessType', form);
  const { resolveName: resolveCategoryName, isLightEquipment } = useTaxonomy();
  const selectedCategory = Form.useWatch('category', form);
  /**
   * The one thing that shortens this wizard. It is read off the chosen TYPE
   * (see ITaxonomyType.isLightEquipment), never asked of the host: they have
   * already answered it by saying what the machine is, and a second question
   * on top would only create a way to answer it wrong.
   *
   * False while the taxonomy is still loading, which is the safe direction:
   * the full form shows for a moment rather than fields for an excavator
   * quietly never being asked for.
   */
  const isLight = isLightEquipment(selectedCategory);

  // Switching from an excavator to a lawnmower has to take the excavator's
  // answers with it. The tiles for wet hire and transport stop being offered
  // the moment the category turns light, but a value already picked would
  // survive in the form and be submitted -- a machine flagged as
  // "con operario" whose operator step no longer exists, or one offering
  // transport that was never configured. Same for hourly pricing, whose six
  // scheduling fields are no longer asked for.
  //
  // Only ever clears, never fills anything in: going the other way (light to
  // heavy) just brings the questions back, empty, which is correct.
  useEffect(() => {
    if (!isLight) return;
    const hireModes: string[] = form.getFieldValue('hireModesList') ?? [];
    const dryOnly = hireModes.filter((mode) => mode === 'DryHireAvailable');
    if (dryOnly.length !== hireModes.length) {
      form.setFieldValue('hireModesList', dryOnly);
      setHasWetHire(false);
      setHasDelivery(false);
    }
    const mode = form.getFieldValue('rentalMode');
    if (mode === 'Hourly' || mode === 'Both') {
      form.setFieldValue('rentalMode', undefined);
      setRentalMode(undefined);
    }
  }, [isLight, form]);
  const selectedBrand = Form.useWatch('manufacturerBrand', form);
  const selectedModel = Form.useWatch('modelName', form);
  const selectedCity = Form.useWatch('city', form);
  const specConfig = getMachinerySpecConfig(selectedCategory, isLight);
  const customSpecFields = getCustomSpecFields(selectedCategory);

  // Título autogeneration (Tarea A, approach (a)): watches category/brand/
  // model/city regardless of which phase is currently mounted, so it keeps
  // the title in sync with Paso 1 (locationSpecs) the moment those fields
  // change -- title itself only ever gets shown on the wizard's LAST phase
  // now (story), so by the time the host sees it, it's already sitting
  // fully computed in the form store; nothing recomputes it "on arrival".
  // Only while the host hasn't overridden it by hand: lastAutoTitleRef
  // remembers what WE last wrote there, and as long as the field still holds
  // that exact value (or is empty), it's safe to keep regenerating; the
  // moment it diverges (the host typed their own title), autogeneration
  // stops touching it for good.
  const lastAutoTitleRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (isEdit || !selectedCategory) return;
    const categoryLabel = resolveCategoryName(selectedCategory);
    if (!categoryLabel) return;
    const namePart = [categoryLabel, selectedBrand, selectedModel].filter(Boolean).join(' ');
    const candidate = selectedCity ? `${namePart} · ${selectedCity}` : namePart;
    if (!candidate) return;
    const currentTitle = form.getFieldValue('title');
    if (!currentTitle || currentTitle === lastAutoTitleRef.current) {
      form.setFieldsValue({ title: candidate });
      lastAutoTitleRef.current = candidate;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedBrand, selectedModel, selectedCity]);

  useEffect(() => {
    getCountries()
      .then(setCountries)
      .catch(() => setCountries([]));
  }, []);

  /**
   * Whether the <Form> element is actually on screen.
   *
   * It is not, while this page is still showing its spinner (see the early
   * return further down), and setFieldsValue on a useForm instance with
   * nothing attached to it is a SILENT no-op -- which is how copying the
   * previous listing would have worked only when the host profile happened to
   * load faster than the listings did. Exactly the sort of bug that works on
   * the machine it was written on.
   */
  const formReady = !loading && (isEdit || (!hostProfileLoading && hasHostProfile));

  /** The listing being copied from, kept so "publicar otra" can copy it again without a second fetch. */
  const previousListingRef = useRef<IHeavyDutyMachinery | undefined>(undefined);
  const inheritedOnceRef = useRef(false);

  /**
   * Whatever this host repeats between one machine and the next, taken from
   * their own last listing.
   *
   * The cheapest part of the whole publishing flow and the one with no chance
   * of being wrong: their yard, their delivery terms and their rules are not
   * something to look up, they are sitting in the listing they made last
   * month. See inheritFromListing for what it refuses to carry over -- the
   * price and the deposit, which are the two that would go wrong in silence.
   */
  const applyPreviousListing = (previous: IHeavyDutyMachinery) => {
    const { values, filledFields } = inheritFromListing(
      previous,
      form.getFieldsValue(true) as Record<string, unknown>,
      WIZARD_DEFAULTS
    );
    if (filledFields.length === 0) return;

    form.setFieldsValue(values as Partial<HeavyDutyMachineryFormValues>);

    // setFieldsValue does not fire onValuesChange, so every piece of state fed
    // from there rather than watched has to be told by hand -- same reason as
    // handleAiApplied below. These decide which phases even exist, so getting
    // them wrong strands somebody on a step that should not be in their flow.
    const modes = (values.hireModesList as string[] | undefined) ?? [];
    if (modes.length > 0) {
      setHasWetHire(modes.includes('WetHireHostOperatorAvailable') || modes.includes('WetHireThirdPartyAvailable'));
      setHasDelivery(modes.includes('DeliveryAvailable'));
    }
    if (values.rentalMode) setRentalMode(values.rentalMode as string);
    if (values.hostCurrencyCode) setHostCurrencyCode(values.hostCurrencyCode as string);

    setInheritedFields(filledFields);
  };

  // Never in edit mode: an existing listing's fields are already answered.
  useEffect(() => {
    if (isEdit || !formReady || inheritedOnceRef.current) return;
    inheritedOnceRef.current = true;

    let cancelled = false;
    getMyHeavyDutyMachineries()
      .then((listings) => {
        if (cancelled) return;
        const previous = mostRecentListing(listings);
        if (!previous) return;
        previousListingRef.current = previous;
        applyPreviousListing(previous);
      })
      // A host with no listings yet, or an endpoint having a bad minute:
      // either way there is simply nothing to copy, and the wizard is exactly
      // what it was before. Never worth a message.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, formReady]);

  // This host's own staff roster -- feeds OperatorAssignmentPicker in the
  // 'operator' phase. Fetched once regardless of whether that phase ends up
  // visible (hasWetHire starts false) since ticking the wet-hire tile can
  // bring it into the flow at any point.
  useEffect(() => {
    getMyOperators()
      .then(setOperators)
      .catch(() => setOperators([]))
      .finally(() => setOperatorsLoading(false));
  }, []);

  // This host's own accessory/insurance catalogs -- feed
  // AccessoryAssignmentPicker/InsurancePolicyAssignmentPicker in the
  // 'extras' phase. Same "fetch once regardless of visibility" reasoning as
  // operators above ('extras' has no toggle gating it, but the fetch still
  // shouldn't block phase navigation).
  useEffect(() => {
    getMyMachineryAccessories()
      .then(setAccessories)
      .catch(() => setAccessories([]))
      .finally(() => setAccessoriesLoading(false));
  }, []);

  useEffect(() => {
    getMyHostInsurancePolicies()
      .then(setInsurancePolicies)
      .catch(() => setInsurancePolicies([]))
      .finally(() => setInsurancePoliciesLoading(false));
  }, []);

  // This host's own transport fleet -- feeds the deliveryVehicleId picker in
  // the 'delivery' phase. Same "fetch once regardless of visibility" reasoning
  // as operators above.
  useEffect(() => {
    getMyTransportVehicles()
      .then(setTransportVehicles)
      .catch(() => setTransportVehicles([]));
  }, []);

  // Preselects hostCurrencyCode from the chosen country's own currency, same
  // "only while the host hasn't overridden it by hand" guard as the title
  // autogeneration above -- lastAutoCurrencyRef remembers what WE last wrote,
  // so a host who picks a different currency than their country's default
  // never gets overwritten back to it on a later re-render.
  const lastAutoCurrencyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!selectedCountryCode || countries.length === 0) return;
    const currencyCode = countries.find((c) => c.alpha2Code === selectedCountryCode)?.currencyCode;
    if (!currencyCode) return;
    const currentValue = form.getFieldValue('hostCurrencyCode');
    if (!currentValue || currentValue === lastAutoCurrencyRef.current) {
      form.setFieldsValue({ hostCurrencyCode: currencyCode });
      setHostCurrencyCode(currencyCode);
      lastAutoCurrencyRef.current = currencyCode;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode, countries]);

  // Deduplicated by currencyCode, same as CurrencySection.tsx's own Settings selector.
  const currencyOptions = Array.from(new Map(countries.map((c) => [c.currencyCode, c])).values())
    .filter((c) => !!c.currencyCode)
    .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode))
    .map((c) => ({ value: c.currencyCode, label: `${c.currencyCode} — ${c.currency}` }));

  const requiredRule = { required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) };

  // The accessRules/rulesPhotos numeric rows relied on flex-wrap + a 180px
  // minWidth per field to lay out side-by-side on desktop, where the left
  // illustration panel (see below) never leaves room for more than a couple
  // per row anyway. On mobile that panel is gone entirely and the row would
  // otherwise try to cram up to 6 fields across a much narrower screen --
  // full-width stacked fields instead, since several of these labels
  // (e.g. "Con cuánta antelación se puede reservar (días)") are long enough
  // that a 2-column grid would wrap them across 2-3 lines each.
  const numericFieldItemStyle: React.CSSProperties = isMobile ? { width: '100%' } : { flex: 1, minWidth: 180 };

  // Translates a HeavyDutyMachineryOption[] into antd Select options (still used for
  // the one field -- Timezone -- that stayed a dropdown: 11 country names
  // aren't meaningfully representable as icon tiles).
  const opts = (options: HeavyDutyMachineryOption[]) => options.map((o) => ({ label: translate(t, o.labelKey), value: o.value }));

  useEffect(() => {
    if (!id) return;
    getHeavyDutyMachineryById(id)
      .then((space) => {
        if (!space) {
          message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_NOT_FOUND));
          navigate('/heavy-duty-machinery/mine');
          return;
        }
        const hireModesList = [
          space.dryHireAvailable && 'DryHireAvailable',
          space.wetHireHostOperatorAvailable && 'WetHireHostOperatorAvailable',
          space.wetHireThirdPartyAvailable && 'WetHireThirdPartyAvailable',
          space.deliveryAvailable && 'DeliveryAvailable',
        ].filter((v): v is string => !!v);
        form.setFieldsValue({
          ...space,
          photosText: space.photos ?? [],
          photoThumbnailsText: space.photoThumbnails ?? [],
          videosList: space.videos ?? [],
          hireModesList,
        });
        // setFieldsValue doesn't fire onValuesChange -- the one place that
        // otherwise keeps rentalMode/hasWetHire's state copies in sync (see
        // their own declarations above).
        setRentalMode(space.rentalMode);
        setHasWetHire(space.wetHireHostOperatorAvailable || space.wetHireThirdPartyAvailable);
        setHasDelivery(space.deliveryAvailable);
        setHostCurrencyCode(space.hostCurrencyCode);
        if ((space.photos ?? []).length > 0) firstPhotoCelebratedRef.current = true;
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isEdit || hostProfileLoading || hasHostProfile) return;
    navigate('/host/onboarding', { replace: true });
  }, [isEdit, hostProfileLoading, hasHostProfile, navigate]);

  // First photo ever added to this listing gets a small celebratory beat --
  // guarded so it never refires on every subsequent add/remove, and skipped
  // entirely in edit mode for a space that already had photos (see the
  // load effect above).
  useEffect(() => {
    if (!firstPhotoCelebratedRef.current && (photos ?? []).length > 0) {
      firstPhotoCelebratedRef.current = true;
      firePhotoConfetti();
    }
  }, [photos]);

  // Recomputed fresh every render off the two hire-mode flags above -- see
  // PhaseConfig.visible's own doc comment for why plain index-based
  // goNext/goBack stay correct even as this list grows/shrinks between
  // renders (e.g. the host goes back to locationSpecs and unticks a mode).
  const visiblePhases = useMemo(
    () => PHASES.filter((phase) => !phase.visible || phase.visible({ hasWetHire, hasDelivery, isLightEquipment: isLight })),
    [hasWetHire, hasDelivery, isLight]
  );
  const currentPhase = visiblePhases[phaseIndex];
  const isLastPhase = phaseIndex === visiblePhases.length - 1;

  const goNext = async () => {
    if (!SKIP_WIZARD_VALIDATION_TEMP) {
      try {
        await form.validateFields(getFieldsForPhase(currentPhase.key, rentalMode, selectedCategory, hasWetHire, isLight));
      } catch {
        return; // antd already highlights the invalid fields.
      }
    }
    if (currentPhase.key === 'locationSpecs') fireSpecsConfetti();
    setPhaseIndex((i) => Math.min(i + 1, visiblePhases.length - 1));
  };

  const goBack = () => setPhaseIndex((i) => Math.max(i - 1, 0));

  /**
   * Everything the publishing assistant worked out, dropped into the form.
   *
   * The rule that governs all of it lives in applyMachineryDraft: a
   * suggestion never replaces an answer the host has already given. That is
   * what makes the button safe to press twice, and safe to press after
   * filling half the form by hand.
   */
  /**
   * The assistant's mark for one field, or nothing at all -- so every call
   * site stays one expression next to its own label.
   */
  const aiMark = (field: string) => (
    <AiFilledMark filled={aiFilledFields.has(field)} tone={AI_CHECK_FIELDS.includes(field) ? 'check' : 'normal'} />
  );

  /**
   * Putting back everything copied from the previous listing, and only that.
   *
   * The whole reason the copy is allowed to be this generous. Without a way
   * out, a host whose new machine lives in another yard would have to hunt
   * down ten fields they never filled in and work out which ones were not
   * theirs -- which is worse than having typed them.
   */
  const handleUndoInherited = () => {
    form.setFieldsValue(revertInherited(inheritedFields, WIZARD_DEFAULTS) as Partial<HeavyDutyMachineryFormValues>);

    // Same by-hand resync as where it was applied -- these decide which
    // phases exist, so they cannot be left describing the copy that has just
    // been undone.
    if (inheritedFields.includes('hireModesList')) {
      setHasWetHire(false);
      setHasDelivery(false);
    }
    if (inheritedFields.includes('rentalMode')) setRentalMode(undefined);
    if (inheritedFields.includes('hostCurrencyCode')) setHostCurrencyCode(undefined);

    setInheritedFields([]);
  };

  /**
   * What the identification plate said.
   *
   * Applied on its own rather than through handleAiApplied because it arrives
   * earlier and carries something else: four facts about THIS machine, read
   * off metal, where everything else the assistant proposes is a fact about a
   * model. The chassis number in particular is the whole reason the camera is
   * there.
   */
  const handlePlateRead = (plate: IMachineryPlateResult) => {
    const current = form.getFieldsValue(true) as Record<string, unknown>;
    const { values, filledFields } = applyPlateRead(plate, current);
    if (filledFields.length === 0) return;

    form.setFieldsValue(values as Partial<HeavyDutyMachineryFormValues>);
    setAiFilledFields((previous) => {
      const next = new Set(previous);
      filledFields.forEach((field) => next.add(field));
      return next;
    });
  };

  const handleAiApplied = (applied: MachineryDraftApplied) => {
    const current = form.getFieldsValue(true) as Record<string, unknown>;
    const { values, customSpecs, filledFields } = applyMachineryDraft(
      applied.draft,
      getCustomSpecFields(applied.categoryCode),
      current
    );

    const filled = [...filledFields];

    // Category, brand and model come from the identify step rather than from
    // the data sheet, and go in under the same rule -- only where nothing has
    // been answered.
    const identity: Record<string, unknown> = {};
    const offerIdentity = (field: string, value?: string) => {
      if (!value || !isBlankFormValue(current[field])) return;
      identity[field] = value;
      filled.push(field);
    };
    offerIdentity('category', applied.categoryCode);
    offerIdentity('manufacturerBrand', applied.manufacturerBrand);
    offerIdentity('modelName', applied.modelName);

    form.setFieldsValue({
      ...values,
      ...identity,
      // Merged rather than replaced: the host may have opened the optional
      // specs panel and filled one in before pressing the button.
      customSpecs: { ...((current.customSpecs as Record<string, string | number | boolean>) ?? {}), ...customSpecs },
    } as Partial<HeavyDutyMachineryFormValues>);

    // setFieldsValue does not fire onValuesChange, so the one piece of state
    // fed from there rather than watched has to be told by hand -- see
    // wizardCategory's own comment above.
    const category = (identity.category ?? current.category) as string | undefined;
    if (category) setWizardCategory(category);

    // Added to rather than replaced, and without spreading the Set: this
    // project targets ES5, where spreading one needs downlevelIteration.
    setAiFilledFields((previous) => {
      const next = new Set(previous);
      filled.forEach((field) => next.add(field));
      return next;
    });

    // Said out loud, because an assistant that appears to have done nothing
    // is indistinguishable from one that is broken. It happens honestly: a
    // model nobody has data for, or a host who had already typed everything.
    if (filled.length === 0) message.info(translate(t, TRANSLATIONS.MACHINERY_AI_NOTHING_FILLED));
  };

  /**
   * The first step that still has a required field nobody has answered, or -1.
   *
   * Reads the same per-phase list "Continuar" validates against, so it can
   * never disagree with it about what is required. A presence check rather
   * than antd's own validation on purpose: the fields of every other phase
   * are unmounted, and validateFields silently passes a rule that is not
   * currently registered.
   */
  const firstIncompletePhaseIndex = () =>
    visiblePhases.findIndex((phase) =>
      getFieldsForPhase(phase.key, rentalMode, selectedCategory, hasWetHire, isLight).some((field) =>
        isBlankFormValue(form.getFieldValue(field))
      )
    );

  /**
   * Where somebody should be looking after the assistant has filled the form
   * in: the first hole, or -- when there are none left -- the last step,
   * where the title and description it wrote are, and where Publicar lives.
   *
   * Deliberately never publishes on its own. A machine going live is the
   * host's act, and one tap away from a screen full of numbers they have not
   * read is not the place to make it automatic.
   */
  const goToWhatIsLeft = () => {
    const index = firstIncompletePhaseIndex();
    setPhaseIndex(index >= 0 ? index : visiblePhases.length - 1);
  };

  const handleSubmit = async () => {
    if (!SKIP_WIZARD_VALIDATION_TEMP) {
      try {
        await form.validateFields(getFieldsForPhase(currentPhase.key, rentalMode, selectedCategory, hasWetHire, isLight));
      } catch {
        return;
      }
    }

    const values = form.getFieldsValue(true) as HeavyDutyMachineryFormValues;
    const { photosText, photoThumbnailsText, videosList, hireModesList, customSpecs, operatorIds, accessoryIds, insurancePolicyIds, deliveryVehicleId, ...rest } = values;

    const hireModes = new Set(hireModesList ?? []);

    const payload = {
      ...rest,
      photos: photosText,
      photoThumbnails: photoThumbnailsText,
      videos: videosList,
      dryHireAvailable: hireModes.has('DryHireAvailable'),
      wetHireHostOperatorAvailable: hireModes.has('WetHireHostOperatorAvailable'),
      wetHireThirdPartyAvailable: hireModes.has('WetHireThirdPartyAvailable'),
      deliveryAvailable: hireModes.has('DeliveryAvailable'),
      // OperatorAssignmentPicker's own value is a string[] of Operator.id
      // (same string-id-from-JSON convention as every other entity here) --
      // OperatorIds on the wire is List<int>, same Number() conversion as
      // HeavyDutyMachineryBookingWidget's own accessoryIds/operatorId.
      operatorIds: operatorIds && operatorIds.length > 0 ? operatorIds.map(Number) : undefined,
      // Same string-id-to-number conversion as operatorIds, for the
      // accessory/insurance pickers in the 'extras' phase.
      accessoryIds: accessoryIds && accessoryIds.length > 0 ? accessoryIds.map(Number) : undefined,
      insurancePolicyIds: insurancePolicyIds && insurancePolicyIds.length > 0 ? insurancePolicyIds.map(Number) : undefined,
      // Same string-id-to-number conversion, for the single deliveryVehicleId.
      deliveryVehicleId: deliveryVehicleId ? Number(deliveryVehicleId) : undefined,
      // undefined (not "{}") when the selected category has no curated spec
      // schema, or the host left every field blank -- same "don't send an
      // empty placeholder" convention as everything else optional here.
      customAttributesJson: customSpecs && Object.keys(customSpecs).length > 0 ? JSON.stringify(customSpecs) : undefined,
    };

    // One call per unit, in order, against the endpoint that already exists.
    // Nothing on the server had to learn about this: a machine is still a
    // machine, and three of them are three of them -- which is the only shape
    // the availability calendar, the slot claims and the telemetry understand.
    const units = Math.min(Math.max(unitCount, 1), MAX_UNITS);
    const unitLabel = translate(t, TRANSLATIONS.MACHINERY_UNITS_DEFAULT_REFERENCE);

    let published = 0;
    let firstCreated: IHeavyDutyMachinery | undefined;

    setSubmitting(true);
    try {
      if (isEdit && id) {
        await updateHeavyDutyMachinery(id, payload);
        message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_SAVED_MESSAGE));
        navigate('/heavy-duty-machinery/mine');
      } else {
        for (let index = 0; index < units; index += 1) {
          const identity = unitIdentityFor(
            index,
            units,
            {
              serialNumber: payload.serialNumber,
              internalReference: payload.internalReference,
              engineHoursAtListing: payload.engineHoursAtListing,
            },
            unitLabel
          );

          // eslint-disable-next-line no-await-in-loop
          const created = await createHeavyDutyMachinery({ ...payload, ...identity } as ICreateHeavyDutyMachineryRequest);
          published += 1;
          if (!firstCreated) firstCreated = created;
        }

        firePublishConfetti();
        if (units > 1) message.success(t(TRANSLATIONS.MACHINERY_UNITS_PUBLISHED, { units }));
        setCelebration(firstCreated);
      }
    } catch (error: any) {
      // Some of them went live before it broke. Saying "no se ha podido" flatly
      // would be a lie AND would send the host off to publish the whole batch
      // again -- so it says how many made it and celebrates those.
      if (published > 0 && firstCreated) {
        message.warning(t(TRANSLATIONS.MACHINERY_UNITS_PARTIAL, { published, total: units }));
        firePublishConfetti();
        setCelebration(firstCreated);
      } else {
        message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_SAVE_ERROR));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublishAnother = () => {
    form.resetFields();
    firstPhotoCelebratedRef.current = false;
    setCelebration(undefined);
    setPhaseIndex(0);
    // Three of one machine says nothing about how many of the next there are.
    setUnitCount(1);
    setAiFilledFields(new Set());
    setInheritedFields([]);
    setHasWetHire(false);
    setHasDelivery(false);
    setRentalMode(undefined);

    // And the second machine gets the same head start the first one did --
    // this is precisely when somebody is publishing their whole yard in one
    // sitting. resetFields has just put every field back to WIZARD_DEFAULTS,
    // so there is nothing of theirs left to protect and no second fetch to
    // make.
    if (previousListingRef.current) applyPreviousListing(previousListingRef.current);
  };

  if (loading || (!isEdit && (hostProfileLoading || !hasHostProfile))) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (celebration) {
    return (
      <div
        style={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 24,
          background: token.colorBgContainer,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          style={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            background: token.colorPrimary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}
        >
          <IconConfetti size={40} color={token.colorTextLightSolid} />
        </motion.div>
        <h1 style={{ margin: '0 0 12px' }}>{translate(t, TRANSLATIONS.WIZARD_CELEBRATION_TITLE)}</h1>
        <Text type="secondary" style={{ maxWidth: 460, display: 'block', marginBottom: 32, fontSize: 15 }}>
          {translate(t, TRANSLATIONS.WIZARD_CELEBRATION_MESSAGE)}
        </Text>
        {/* Neither button wraps its own text, so on a narrow screen the pair
            together (~"Publicar otro espacio" + "Ver mi espacio publicado")
            is wider than the ~24px-padded space left for them -- without
            wrap they simply overflowed past that padding, right up against
            (and slightly past) the screen edges. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
          <Button shape="round" size="large" onClick={handlePublishAnother}>
            {translate(t, TRANSLATIONS.WIZARD_CELEBRATION_PUBLISH_ANOTHER_BUTTON)}
          </Button>
          <Button shape="round" size="large" type="primary" onClick={() => navigate(`/heavy-duty-machinery/${celebration.id}`)}>
            {translate(t, TRANSLATIONS.WIZARD_CELEBRATION_VIEW_SPACE_BUTTON)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', width: '100%', height: isMobile ? '100dvh' : '100vh', overflow: 'hidden' }}>
      {/* Left panel -- fixed, one per-phase illustration + title + progress.
          Desktop/tablet only: on mobile there's no room for a purely
          decorative panel, so it's dropped entirely rather than shrunk --
          see the compact step indicator inside the right panel's header
          below, which takes over its "sense of progress" role instead. */}
      {!isMobile && (
        <div
          style={{
            width: '38%',
            minWidth: 320,
            maxWidth: 460,
            flexShrink: 0,
            background: currentPhase.background,
            color: '#fff',
            padding: '48px 40px 40px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            transition: 'background-color 400ms ease',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPhase.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {t(TRANSLATIONS.WIZARD_STEP_LABEL, { current: phaseIndex + 1, total: visiblePhases.length })}
              </Text>
              <h1 style={{ color: '#fff', fontSize: 30, margin: '8px 0 12px' }}>{translate(t, currentPhase.titleKey)}</h1>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, lineHeight: 1.5 }}>{translate(t, currentPhase.subtitleKey)}</Text>
            </motion.div>
          </AnimatePresence>

          {/* Progress bar -- one segment per phase, the current one wider and
              fully bright, past ones bright-but-slim, upcoming ones dim. */}
          <div style={{ display: 'flex', gap: 6, marginTop: 32 }}>
            {visiblePhases.map((phase, i) => (
              <div
                key={phase.key}
                style={{
                  height: 4,
                  borderRadius: 2,
                  flex: i === phaseIndex ? 2.4 : 1,
                  background: i <= phaseIndex ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)',
                  transition: 'flex 300ms ease, background-color 300ms ease',
                }}
              />
            ))}
          </div>

          <div style={{ marginTop: 'auto' }}>
            <WizardIllustration icon={currentPhase.icon} phaseKey={currentPhase.key} />
          </div>
        </div>
      )}

      {/* Right panel -- the current phase's own fields, scrollable, with a
          sticky Atrás/Continuar footer. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {isMobile ? (
          // Mobile header: step label + close button on one row, then the
          // current phase's title/subtitle (the same copy the desktop panel
          // shows), then a compact progress bar re-colored for a light
          // background -- together these take over what the illustration
          // panel communicated on desktop, without spending any horizontal
          // space on something purely decorative.
          <div style={{ padding: '16px 20px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <Text style={{ color: token.colorTextTertiary, fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {t(TRANSLATIONS.WIZARD_STEP_LABEL, { current: phaseIndex + 1, total: visiblePhases.length })}
              </Text>
              <button
                type="button"
                aria-label={translate(t, TRANSLATIONS.HOST_ONBOARDING_CANCEL)}
                onClick={() => navigate('/heavy-duty-machinery/mine')}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: token.colorTextTertiary, padding: 11, flexShrink: 0 }}
              >
                <IconX size={22} />
              </button>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentPhase.key}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <h1 style={{ fontSize: 20, margin: '6px 0 4px' }}>{translate(t, currentPhase.titleKey)}</h1>
                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
                  {translate(t, currentPhase.subtitleKey)}
                </Text>
              </motion.div>
            </AnimatePresence>

            <div style={{ display: 'flex', gap: 4, marginTop: 12, marginBottom: 4 }}>
              {visiblePhases.map((phase, i) => (
                <div
                  key={phase.key}
                  style={{
                    height: 4,
                    borderRadius: 2,
                    flex: i === phaseIndex ? 2.4 : 1,
                    background: i <= phaseIndex ? TEAL : token.colorFillTertiary,
                    transition: 'flex 300ms ease, background-color 300ms ease',
                  }}
                />
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '20px 40px 0' }}>
            <button
              type="button"
              aria-label={translate(t, TRANSLATIONS.HOST_ONBOARDING_CANCEL)}
              onClick={() => navigate('/heavy-duty-machinery/mine')}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: token.colorTextTertiary, padding: 11 }}
            >
              <IconX size={22} />
            </button>
          </div>
        )}

        <div ref={contentRef} style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '8px 20px 24px' : '8px 56px 32px' }}>
          <Form
            form={form}
            layout="vertical"
            component={false}
            initialValues={WIZARD_DEFAULTS}
            onValuesChange={(changedValues) => {
              if ('rentalMode' in changedValues) setRentalMode(changedValues.rentalMode);
              if ('hireModesList' in changedValues) {
                const list: string[] = changedValues.hireModesList ?? [];
                setHasWetHire(list.includes('WetHireHostOperatorAvailable') || list.includes('WetHireThirdPartyAvailable'));
                setHasDelivery(list.includes('DeliveryAvailable'));
              }
              if ('hostCurrencyCode' in changedValues) setHostCurrencyCode(changedValues.hostCurrencyCode);
              if ('category' in changedValues) setWizardCategory(changedValues.category);
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPhase.key}
                initial={{ opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -28 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ maxWidth: 640 }}
              >
                {/* Shown on every step from the moment the assistant fills
                    anything in, because what it says is true on every step:
                    some of these numbers are suggestions. It also names the
                    two things it never proposes -- the price and where the
                    machine is -- so nobody goes looking for them among the
                    sparkles. */}
                {aiFilledFields.size > 0 && (
                  <div
                    style={{
                      marginBottom: 24,
                      padding: '12px 14px',
                      borderRadius: 14,
                      background: TEAL_SOFT_BG,
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                    }}
                  >
                    <IconSparkles size={16} color={TEAL} style={{ flexShrink: 0, marginTop: 3 }} />
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <Text style={{ fontSize: 13, display: 'block' }}>
                        {t(TRANSLATIONS.MACHINERY_AI_BANNER, { fields: aiFilledFields.size })}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {translate(t, TRANSLATIONS.MACHINERY_AI_BANNER_YOURS)}
                      </Text>
                    </div>
                    <Button size="small" shape="round" onClick={goToWhatIsLeft} disabled={submitting}>
                      {translate(t, TRANSLATIONS.MACHINERY_AI_GO_TO_MISSING)}
                    </Button>
                  </div>
                )}

                {currentPhase.key === 'story' && (
                  <>
                    <FieldGroup label="">
                      <Form.Item name="title" style={{ marginBottom: 20 }}>
                        <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_TITLE_LABEL)} maxLength={200} disabled={submitting} />
                      </Form.Item>
                    </FieldGroup>
                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DESCRIPTION_LABEL)}>
                      <Form.Item name="description" rules={[requiredRule]}>
                        <EmojiTextArea
                          rows={5}
                          maxLength={4000}
                          disabled={submitting}
                          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DESCRIPTION_PLACEHOLDER)}
                        />
                      </Form.Item>
                    </FieldGroup>
                  </>
                )}

                {currentPhase.key === 'locationSpecs' && (
                  <>
                    {/* Only when publishing, never when editing: an existing
                        listing's fields are already answered, and the one
                        thing this must never do is talk over them. */}
                    {!isEdit && (
                      <MachineryDraftAssistant
                        currentCategory={selectedCategory}
                        disabled={submitting}
                        onApplied={handleAiApplied}
                        onPlateRead={handlePlateRead}
                      />
                    )}

                    {/* Only on this step, and only until it is undone. Most of
                        what it copied lands right here (the yard), and a
                        banner repeated on all seven steps is chrome rather
                        than information -- so the copy names where the rest
                        of it went instead. */}
                    {inheritedFields.length > 0 && (
                      <div
                        style={{
                          marginBottom: 32,
                          padding: '12px 14px',
                          borderRadius: 14,
                          background: SURFACE_1_SOFT,
                          display: 'flex',
                          gap: 10,
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                        }}
                      >
                        <IconCopy size={16} color={token.colorTextSecondary} style={{ flexShrink: 0, marginTop: 3 }} />
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <Text style={{ fontSize: 13, display: 'block' }}>
                            {translate(t, TRANSLATIONS.MACHINERY_INHERITED_BANNER)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {translate(t, TRANSLATIONS.MACHINERY_INHERITED_BANNER_NOT)}
                          </Text>
                        </div>
                        <Button size="small" shape="round" onClick={handleUndoInherited} disabled={submitting}>
                          {translate(t, TRANSLATIONS.MACHINERY_INHERITED_UNDO)}
                        </Button>
                      </div>
                    )}

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_LOCATION_LABEL)}>
                      <Form.Item
                        name="city"
                        rules={[
                          requiredRule,
                          {
                            validator: () =>
                              form.getFieldValue('countryCode')
                                ? Promise.resolve()
                                : Promise.reject(new Error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_LOCATION_NOT_SELECTED))),
                          },
                        ]}
                      >
                        <LocationAutocomplete
                          disabled={submitting}
                          includeCountryInLabel
                          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_LOCATION_PLACEHOLDER)}
                          onChange={() => form.setFieldsValue({ countryCode: undefined })}
                          onSelectLocation={(location) => {
                            form.setFieldsValue({
                              province: location.province,
                              region: location.region,
                              country: location.country,
                              countryCode: location.countryCode,
                              formattedAddress: location.formattedAddress,
                              administrativeLevel: location.administrativeLevel,
                              latitude: location.latitude,
                              longitude: location.longitude,
                            });
                          }}
                        />
                      </Form.Item>
                      {selectedCountryName && (
                        <Text type="secondary" style={{ display: 'block', marginTop: -8, marginBottom: 8 }}>
                          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_COUNTRY_LABEL)}: {selectedCountryName}
                        </Text>
                      )}
                      {selectedLatitude != null && selectedLongitude != null && (
                        <div style={{ marginTop: 8 }}>
                          <StaticMap latitude={selectedLatitude} longitude={selectedLongitude} />
                        </div>
                      )}
                      <Form.Item name="province" hidden><input /></Form.Item>
                      <Form.Item name="region" hidden><input /></Form.Item>
                      <Form.Item name="country" hidden><input /></Form.Item>
                      <Form.Item name="countryCode" hidden><input /></Form.Item>
                      <Form.Item name="formattedAddress" hidden><input /></Form.Item>
                      <Form.Item name="administrativeLevel" hidden><input /></Form.Item>
                      <Form.Item name="latitude" hidden><input /></Form.Item>
                      <Form.Item name="longitude" hidden><input /></Form.Item>
                    </FieldGroup>

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_HOST_CURRENCY_LABEL)}>
                      <Form.Item name="hostCurrencyCode" style={{ marginBottom: 4 }}>
                        <Select
                          showSearch
                          disabled={submitting}
                          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_HOST_CURRENCY_PLACEHOLDER)}
                          filterOption={(input, option: any) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                          options={currencyOptions}
                        />
                      </Form.Item>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_HOST_CURRENCY_HELP)}
                      </Text>
                    </FieldGroup>

                    <FieldGroup label="">
                      <Form.Item name="privateAddress" style={{ marginBottom: 4 }}>
                        <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRIVATE_ADDRESS_LABEL)} disabled={submitting} />
                      </Form.Item>
                      <Text type="secondary" style={{ fontSize: 12 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRIVATE_ADDRESS_HELP)}</Text>
                    </FieldGroup>

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CATEGORY_LABEL)}>
                      <Form.Item name="category" rules={[requiredRule]}>
                        <TaxonomyPicker disabled={submitting} />
                      </Form.Item>
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        <Form.Item name="manufacturerBrand" style={numericFieldItemStyle}>
                          <ManufacturerBrandSelect
                            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MANUFACTURER_BRAND_LABEL)}
                            disabled={submitting}
                          />
                        </Form.Item>
                        <div style={numericFieldItemStyle}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MODEL_NAME_LABEL)}
                          </Text>
                          <Form.Item name="modelName" style={{ marginBottom: 0 }}>
                            <Input disabled={submitting} />
                          </Form.Item>
                        </div>
                      </div>
                    </FieldGroup>

                    {/* Bastidor y referencia interna. Los dos son del host y
                        no se publican nunca (el servidor ni los manda a un
                        invitado), así que van juntos y con su aviso: sin
                        bastidor, una máquina que no vuelve no se puede
                        denunciar. Opcionales, porque una hidrolimpiadora
                        puede no tener número que copiar. */}
                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_IDENTIFICATION_GROUP)}>
                      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 10 }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_IDENTIFICATION_HELP)}
                      </Text>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        <div style={numericFieldItemStyle}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_SERIAL_NUMBER_LABEL)}{aiMark('serialNumber')}
                          </Text>
                          <Form.Item name="serialNumber" style={{ marginBottom: 0 }}>
                            <Input disabled={submitting} />
                          </Form.Item>
                        </div>
                        <div style={numericFieldItemStyle}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_INTERNAL_REFERENCE_LABEL)}
                          </Text>
                          <Form.Item name="internalReference" style={{ marginBottom: 0 }}>
                            <Input disabled={submitting} />
                          </Form.Item>
                        </div>
                      </div>
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        <Form.Item name="yearManufactured" style={numericFieldItemStyle}>
                          <PillNumberField label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_YEAR_MANUFACTURED_LABEL)}{aiMark('yearManufactured')}</>} min={1950} disabled={submitting} />
                        </Form.Item>
                        {/* Horas de motor sólo donde significan algo. Un
                            cortacésped no lleva horímetro, y el año de
                            fabricación sí se sigue preguntando a todos. */}
                        {specConfig.engineHoursAtListing !== 'hidden' && (
                          <Form.Item name="engineHoursAtListing" style={numericFieldItemStyle}>
                          <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_ENGINE_HOURS_LABEL)} min={0} disabled={submitting} />
                          </Form.Item>
                        )}
                      </div>
                    </FieldGroup>

                    {/* Peso, potencia y alcance: las tres cifras que
                        definen una máquina de obra y que nadie sabe de su
                        cortacésped. specConfig las oculta enteras para
                        equipo ligero (ver machinerySpecsByCategory), así que
                        el grupo desaparece en vez de quedarse vacío. */}
                    {!isLight && (
                      <FieldGroup label="">
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                          <Form.Item
                            name="weightTons"
                            rules={specConfig.weightTons === 'required' ? [requiredRule] : undefined}
                            style={numericFieldItemStyle}
                          >
                            <PillNumberField label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WEIGHT_TONS_LABEL)}{aiMark('weightTons')}</>} min={0} step={0.1} disabled={submitting} />
                          </Form.Item>
                          <Form.Item
                            name="powerHp"
                            rules={specConfig.powerHp === 'required' ? [requiredRule] : undefined}
                            style={numericFieldItemStyle}
                          >
                            <PillNumberField label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_POWER_HP_LABEL)}{aiMark('powerHp')}</>} min={0} disabled={submitting} />
                          </Form.Item>
                          {specConfig.reachMeters !== 'hidden' && (
                            <Form.Item
                              name="reachMeters"
                              rules={specConfig.reachMeters === 'required' ? [requiredRule] : undefined}
                              style={numericFieldItemStyle}
                            >
                              <PillNumberField label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_REACH_METERS_LABEL)}{aiMark('reachMeters')}</>} min={0} step={0.1} disabled={submitting} />
                            </Form.Item>
                          )}
                        </div>
                      </FieldGroup>
                    )}

                    {/* Propulsión, combustible, cotas de transporte y
                        enganche rápido. Todo esto existe para decidir si una
                        máquina cabe en un camión y qué la mueve; para algo
                        que va en el maletero no hay nada que decidir. */}
                    {!isLight && (
                      <FieldGroup label={translate(t, TRANSLATIONS.MACHINERY_SPEC_SECTION_TITLE)}>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                          <div style={{ flex: 1, minWidth: isMobile ? '100%' : 260 }}>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                              {translate(t, TRANSLATIONS.MACHINERY_SPEC_PROPULSION_TYPE_LABEL)}{aiMark('propulsionType')}
                            </Text>
                            <Form.Item name="propulsionType" style={{ marginBottom: 0 }}>
                              <WizardTileGrid options={PROPULSION_TYPE_OPTIONS} disabled={submitting} minTileWidth={100} />
                            </Form.Item>
                          </div>
                          <div style={{ flex: 1, minWidth: isMobile ? '100%' : 260 }}>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                              {translate(t, TRANSLATIONS.MACHINERY_SPEC_FUEL_TYPE_LABEL)}{aiMark('fuelType')}
                            </Text>
                            <Form.Item name="fuelType" style={{ marginBottom: 0 }}>
                              <WizardTileGrid options={FUEL_TYPE_OPTIONS} disabled={submitting} minTileWidth={100} />
                            </Form.Item>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row', marginTop: 16 }}>
                          <Form.Item name="transportLengthM" style={numericFieldItemStyle}>
                            <PillNumberField label={<>{translate(t, TRANSLATIONS.MACHINERY_SPEC_TRANSPORT_LENGTH_LABEL)}{aiMark('transportLengthM')}</>} min={0} step={0.1} disabled={submitting} />
                          </Form.Item>
                          <Form.Item name="transportWidthM" style={numericFieldItemStyle}>
                            <PillNumberField label={<>{translate(t, TRANSLATIONS.MACHINERY_SPEC_TRANSPORT_WIDTH_LABEL)}{aiMark('transportWidthM')}</>} min={0} step={0.1} disabled={submitting} />
                          </Form.Item>
                          <Form.Item name="transportHeightM" style={numericFieldItemStyle}>
                            <PillNumberField label={<>{translate(t, TRANSLATIONS.MACHINERY_SPEC_TRANSPORT_HEIGHT_LABEL)}{aiMark('transportHeightM')}</>} min={0} step={0.1} disabled={submitting} />
                          </Form.Item>
                        </div>
                        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Form.Item name="hasQuickHitch" valuePropName="checked" style={{ marginBottom: 0 }}>
                            <Switch disabled={submitting} />
                          </Form.Item>
                          <Text>{translate(t, TRANSLATIONS.MACHINERY_SPEC_HAS_QUICK_HITCH_LABEL)}{aiMark('hasQuickHitch')}</Text>
                        </div>
                      </FieldGroup>
                    )}

                    {customSpecFields.length > 0 && (
                      <div style={{ marginBottom: 40 }}>
                        <Collapse
                          ghost
                          className="heavyDutyMachinery-optional-collapse"
                          expandIconPosition="end"
                          expandIcon={({ isActive }) => (
                            <IconChevronDown
                              size={18}
                              color={token.colorTextSecondary}
                              style={{ transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}
                            />
                          )}
                          items={[
                            {
                              key: 'customSpecs',
                              label: (
                                <Text strong style={{ fontSize: 20 }}>
                                  {translate(t, TRANSLATIONS.MACHINERY_SPEC_CUSTOM_SECTION_TITLE)}
                                </Text>
                              ),
                              children: (
                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                                  {customSpecFields.map((field) => {
                            const fieldLabel = (
                              <>
                                {`${translate(t, field.labelKey)}${field.unitKey ? ` (${translate(t, field.unitKey)})` : ''}`}
                                {aiMark(`customSpecs.${field.key}`)}
                              </>
                            );
                            if (field.type === 'boolean') {
                              return (
                                <div key={field.key} style={{ ...numericFieldItemStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <Form.Item name={['customSpecs', field.key]} valuePropName="checked" style={{ marginBottom: 0 }}>
                                    <Switch disabled={submitting} />
                                  </Form.Item>
                                  <Text>{fieldLabel}</Text>
                                </div>
                              );
                            }
                            if (field.type === 'select') {
                              return (
                                <div key={field.key} style={numericFieldItemStyle}>
                                  <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                                    {fieldLabel}
                                  </Text>
                                  <Form.Item name={['customSpecs', field.key]} style={{ marginBottom: 0 }}>
                                    <Select
                                      allowClear
                                      disabled={submitting}
                                      options={(field.options ?? []).map((option) => ({ value: option.value, label: translate(t, option.labelKey) }))}
                                    />
                                  </Form.Item>
                                </div>
                              );
                            }
                            if (field.type === 'number') {
                              return (
                                <Form.Item key={field.key} name={['customSpecs', field.key]} style={numericFieldItemStyle}>
                                  <PillNumberField label={fieldLabel} min={0} step={0.1} disabled={submitting} />
                                </Form.Item>
                              );
                            }
                            return (
                              <div key={field.key} style={numericFieldItemStyle}>
                                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                                  {fieldLabel}
                                </Text>
                                <Form.Item name={['customSpecs', field.key]} style={{ marginBottom: 0 }}>
                                  <Input disabled={submitting} />
                                </Form.Item>
                              </div>
                            );
                                  })}
                                </div>
                              ),
                            },
                          ]}
                        />
                      </div>
                    )}

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RENTAL_AVAILABILITY_LABEL)}>
                      <Form.Item name="hireModesList">
                        <WizardTileGrid options={isLight ? HIRE_MODE_LIGHT_OPTIONS : HIRE_MODE_OPTIONS} multiple disabled={submitting} />
                      </Form.Item>
                    </FieldGroup>

                    {/* Reserva diferida -- a different kind of setting than
                        the hire-modes checklist above (immediate-vs-deferred
                        checkout, not a hire mode), so it's its own plain
                        boolean here rather than folded into hireModesList's
                        own cross-mapping. */}
                    <FieldGroup label="">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Form.Item name="deferredBookingEnabled" valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Switch disabled={submitting} />
                        </Form.Item>
                        <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DEFERRED_BOOKING_LABEL)}</Text>
                      </div>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DEFERRED_BOOKING_HELP)}
                      </Text>
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        <Form.Item name="overtimeHourRate" style={numericFieldItemStyle}>
                          <PillNumberField
                            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_OVERTIME_HOUR_RATE_LABEL)}
                            min={0}
                            step={0.01}
                            addonAfter={hostCurrencyCode || 'EUR'}
                            disabled={submitting}
                          />
                        </Form.Item>
                        <Form.Item name="depositAmount" style={numericFieldItemStyle}>
                          <PillNumberField
                            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DEPOSIT_AMOUNT_LABEL)}
                            min={0}
                            step={0.01}
                            addonAfter={hostCurrencyCode || 'EUR'}
                            disabled={submitting}
                          />
                        </Form.Item>
                      </div>
                    </FieldGroup>
                  </>
                )}

                {currentPhase.key === 'operator' && (
                  <>
                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_ASSIGNMENT_LABEL)}>
                      <Form.Item name="operatorIds" style={{ marginBottom: 4 }}>
                        <OperatorAssignmentPicker
                          operators={operators}
                          loading={operatorsLoading}
                          qualifiedCategory={wizardCategory}
                          disabled={submitting}
                        />
                      </Form.Item>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_ASSIGNMENT_HELP)}
                      </Text>
                    </FieldGroup>

                    <FieldGroup label="">
                      <Form.Item name="maxOperators" rules={[requiredRule]} style={{ marginBottom: 4 }}>
                        <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_OPERATORS_LABEL)} min={1} disabled={submitting} />
                      </Form.Item>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_OPERATORS_HELP)}
                      </Text>
                    </FieldGroup>
                  </>
                )}

                {currentPhase.key === 'delivery' && (
                  <>
                    <FieldGroup label="">
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        <Form.Item name="pricePerKmDelivery" style={numericFieldItemStyle}>
                          <PillNumberField
                            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_PER_KM_DELIVERY_LABEL)}
                            min={0}
                            step={0.01}
                            addonAfter={hostCurrencyCode || 'EUR'}
                            disabled={submitting}
                          />
                        </Form.Item>
                        <Form.Item name="fixedDispatchFee" style={numericFieldItemStyle}>
                          <PillNumberField
                            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_FIXED_DISPATCH_FEE_LABEL)}
                            min={0}
                            step={0.01}
                            addonAfter={hostCurrencyCode || 'EUR'}
                            disabled={submitting}
                          />
                        </Form.Item>
                      </div>
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        <Form.Item name="deliveryVehicleId" style={numericFieldItemStyle}>
                          <PillSelectField
                            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DELIVERY_VEHICLE_LABEL)}
                            options={transportVehicles.filter((v) => v.isActive).map((v) => ({ value: v.id, label: v.name }))}
                            disabled={submitting}
                          />
                        </Form.Item>
                      </div>
                      {transportVehicles.length === 0 ? (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                          <Link to="/heavy-duty-machinery/transport-vehicles" target="_blank" rel="noopener noreferrer">
                            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DELIVERY_VEHICLE_EMPTY_CTA)}
                          </Link>
                        </Text>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DELIVERY_VEHICLE_HELP)}
                        </Text>
                      )}
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        <Form.Item name="maxDeliveryRadiusKm" style={numericFieldItemStyle}>
                          <PillNumberField
                            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_DELIVERY_RADIUS_KM_LABEL)}
                            min={0}
                            step={1}
                            addonAfter="km"
                            disabled={submitting}
                          />
                        </Form.Item>
                        <Form.Item name="minRentalDaysForDelivery" style={numericFieldItemStyle}>
                          <PillNumberField
                            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MIN_RENTAL_DAYS_FOR_DELIVERY_LABEL)}
                            min={1}
                            step={1}
                            disabled={submitting}
                          />
                        </Form.Item>
                      </div>
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Form.Item name="restrictDeliveryToSameCountry" valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Switch disabled={submitting} />
                        </Form.Item>
                        <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RESTRICT_DELIVERY_TO_SAME_COUNTRY_LABEL)}</Text>
                      </div>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RESTRICT_DELIVERY_TO_SAME_COUNTRY_HELP)}
                      </Text>
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Form.Item name="chargeStaffTimeForDelivery" valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Switch disabled={submitting} />
                        </Form.Item>
                        <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CHARGE_STAFF_TIME_FOR_DELIVERY_LABEL)}</Text>
                      </div>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CHARGE_STAFF_TIME_FOR_DELIVERY_HELP)}
                      </Text>
                    </FieldGroup>
                  </>
                )}

                {currentPhase.key === 'extras' && (
                  <>
                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_LINK_SECTION_TITLE)}>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_LINK_SECTION_SUBTITLE)}
                      </Text>
                      <Form.Item name="accessoryIds" style={{ marginBottom: 0 }}>
                        <AccessoryAssignmentPicker
                          accessories={accessories}
                          loading={accessoriesLoading}
                          category={wizardCategory}
                          disabled={submitting}
                        />
                      </Form.Item>
                    </FieldGroup>

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_LINK_SECTION_TITLE)}>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_LINK_SECTION_SUBTITLE)}
                      </Text>
                      <Form.Item name="insurancePolicyIds" style={{ marginBottom: 0 }}>
                        <InsurancePolicyAssignmentPicker
                          policies={insurancePolicies}
                          loading={insurancePoliciesLoading}
                          disabled={submitting}
                        />
                      </Form.Item>
                    </FieldGroup>
                  </>
                )}

                {currentPhase.key === 'accessRules' && (
                  <>
                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_ACCESS_TYPE_LABEL)}>
                      <Form.Item name="accessType" rules={[requiredRule]} style={{ marginBottom: 0 }}>
                        <WizardTileGrid options={ACCESS_TYPE_WIZARD_OPTIONS} disabled={submitting} />
                      </Form.Item>
                      <FieldReinforcement field="accessType" value={accessType} />
                    </FieldGroup>

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RENTAL_MODE_LABEL)}>
                      <Form.Item name="rentalMode" rules={[requiredRule]} style={{ marginBottom: 0 }}>
                        <WizardTileGrid options={isLight ? RENTAL_MODE_LIGHT_OPTIONS : RENTAL_MODE_WIZARD_OPTIONS} disabled={submitting} />
                      </Form.Item>
                      <FieldReinforcement field="rentalMode" value={rentalMode} />
                    </FieldGroup>

                    <FieldGroup label="">
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                        {(rentalMode === 'Hourly' || rentalMode === 'Both') && (
                          <Form.Item name="pricePerHour" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField
                              label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_PER_HOUR_LABEL)}
                              min={0.01}
                              step={0.01}
                              addonAfter={hostCurrencyCode || 'EUR'}
                              disabled={submitting}
                            />
                          </Form.Item>
                        )}
                        {(rentalMode === 'Overnight' || rentalMode === 'Both') && (
                          <Form.Item name="pricePerNight" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField
                              label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_PER_NIGHT_LABEL)}
                              min={0.01}
                              step={0.01}
                              addonAfter={hostCurrencyCode || 'EUR'}
                              disabled={submitting}
                            />
                          </Form.Item>
                        )}
                      </div>
                    </FieldGroup>

                    {(rentalMode === 'Overnight' || rentalMode === 'Both') && (
                      <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DAILY_SCHEDULE_GROUP_LABEL)}>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                          <Form.Item name="maxAdvanceBookingDays" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_ADVANCE_BOOKING_DAYS_LABEL)} min={1} disabled={submitting} />
                          </Form.Item>
                        </div>
                      </FieldGroup>
                    )}

                    {(rentalMode === 'Hourly' || rentalMode === 'Both') && (
                      <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_HOURLY_SCHEDULE_GROUP_LABEL)}>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                          <Form.Item name="bufferMinutes" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_BUFFER_MINUTES_LABEL)} min={0} disabled={submitting} />
                          </Form.Item>
                          <Form.Item name="minAdvanceBookingHours" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MIN_ADVANCE_BOOKING_HOURS_LABEL)} min={0} disabled={submitting} />
                          </Form.Item>
                          <Form.Item name="minBookingDurationMinutes" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MIN_BOOKING_DURATION_LABEL)} min={1} disabled={submitting} />
                          </Form.Item>
                          <Form.Item name="maxBookingDurationMinutes" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_BOOKING_DURATION_LABEL)} min={1} disabled={submitting} />
                          </Form.Item>
                          <Form.Item name="slotGranularityMinutes" rules={[requiredRule]} style={numericFieldItemStyle}>
                            <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_SLOT_GRANULARITY_LABEL)} min={0} disabled={submitting} />
                          </Form.Item>
                        </div>
                      </FieldGroup>
                    )}

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PARKING_LABEL)}>
                      <Form.Item name="parking" rules={[requiredRule]}>
                        <WizardTileGrid options={PARKING_OPTIONS} disabled={submitting} />
                      </Form.Item>
                    </FieldGroup>
                  </>
                )}

                {currentPhase.key === 'rulesPhotos' && (
                  <>
                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CANCELLATION_POLICY_LABEL)}>
                      <Form.Item name="cancellationPolicy" rules={[requiredRule]}>
                        <WizardTileGrid options={CANCELLATION_POLICY_OPTIONS} disabled={submitting} />
                      </Form.Item>
                    </FieldGroup>

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_TIMEZONE_LABEL)}>
                      <Form.Item name="timezone" rules={[requiredRule]}>
                        <Select options={opts(TIMEZONE_OPTIONS)} disabled={submitting} />
                      </Form.Item>
                    </FieldGroup>

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WELCOME_MESSAGE_LABEL)}>
                      <Form.Item name="welcomeMessage" style={{ marginBottom: 4 }}>
                        <EmojiTextArea
                          rows={4}
                          maxLength={2000}
                          disabled={submitting}
                          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WELCOME_MESSAGE_PLACEHOLDER)}
                        />
                      </Form.Item>
                      <Text type="secondary" style={{ fontSize: 12 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WELCOME_MESSAGE_HELP)}</Text>
                    </FieldGroup>

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PHOTOS_LABEL)}>
                      <PhotoUpload
                        domain="spaces"
                        value={photos}
                        thumbnails={photoThumbnails}
                        onChange={(newPhotos, newThumbnails) =>
                          form.setFieldsValue({ photosText: newPhotos, photoThumbnailsText: newThumbnails })
                        }
                        disabled={submitting}
                      />
                      <Form.Item name="photosText" hidden><input /></Form.Item>
                      <Form.Item name="photoThumbnailsText" hidden><input /></Form.Item>
                    </FieldGroup>

                    <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_VIDEOS_LABEL)}>
                      <VideoUpload
                        domain="spaces"
                        value={videos}
                        onChange={(newVideos) => form.setFieldsValue({ videosList: newVideos })}
                        disabled={submitting}
                      />
                      <Form.Item name="videosList" hidden><input /></Form.Item>
                    </FieldGroup>

                    {/* The last thing asked, right before Publicar, and never
                        when editing. At the start it would be one more
                        question before anything had happened; here it is a
                        multiplier on work already done. */}
                    {!isEdit && (
                      <FieldGroup label={translate(t, TRANSLATIONS.MACHINERY_UNITS_LABEL)}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {Array.from({ length: MAX_UNITS }, (_, i) => i + 1).map((count) => (
                            <Button
                              key={count}
                              shape="circle"
                              size="large"
                              disabled={submitting}
                              type={unitCount === count ? 'primary' : 'default'}
                              onClick={() => setUnitCount(count)}
                            >
                              {count}
                            </Button>
                          ))}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10, lineHeight: 1.45 }}>
                          {unitCount > 1
                            ? t(TRANSLATIONS.MACHINERY_UNITS_HELP_MANY, { units: unitCount })
                            : translate(t, TRANSLATIONS.MACHINERY_UNITS_HELP_ONE)}
                        </Text>
                      </FieldGroup>
                    )}
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </Form>
        </div>

        <div
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
            // `env(safe-area-inset-bottom, 0px)` resolves to 0 in every
            // regular browser today (nothing to reserve for), and only
            // starts adding real space if this ever runs inside a native
            // WebView (e.g. Capacitor) where the iPhone gesture bar can
            // otherwise sit on top of these buttons -- safe to ship
            // unconditionally now, ahead of actually needing it.
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            paddingLeft: isMobile ? 20 : 56,
            paddingRight: isMobile ? 20 : 56,
            display: 'flex',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          {phaseIndex > 0 ? (
            <Button shape="round" size="large" onClick={goBack} disabled={submitting}>
              {translate(t, TRANSLATIONS.HOST_ONBOARDING_BACK)}
            </Button>
          ) : (
            <div />
          )}
          {isLastPhase ? (
            <Button shape="round" size="large" type="primary" onClick={handleSubmit} loading={submitting}>
              {translate(t, isEdit ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_SAVE_CHANGES_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PUBLISH_BUTTON)}
            </Button>
          ) : (
            <Button shape="round" size="large" type="primary" onClick={goNext}>
              {translate(t, TRANSLATIONS.WIZARD_CONTINUE_BUTTON)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeavyDutyMachineryForm;
